import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gql } from 'graphql-request'; // Or your preferred gql tag
import { useQuery } from '@hooks/graphql'
import { Button } from '@/components/ui/button';

// --- GraphQL Query Definition (Reverted) ---
// Only requests fields needed for status check and final ID
const CHECK_GENERATION_STATUS_QUERY = gql`
  query CheckGenerationStatus($id: ID!) {
    educatorProjectGeneration(id: $id) {
      id
      status
      educatorProject {
        id
        name
      }
    }
  }
`;

// Define the messages and emojis
const loadingMessages = [
    { emoji: '✂️', text: 'Cutting out the busywork...' },
    { emoji: '📚', text: 'Turning raw content into teachable moments...' },
    { emoji: '🎯', text: 'Building clear learning outcomes...' },
    { emoji: '🧩', text: 'Structuring your content into blocks...' },
    { emoji: '💭', text: 'Generating thought-provoking questions...' },
    { emoji: '🔍', text: 'Checking content for clarity and coverage...' },
];

// --- Constants ---
const FADE_DURATION = 500; // Text fade animation (ms)
const POLLING_INTERVAL = 2000; // How often to check status (ms)
// Durations for progress calculation (seconds)
const INITIAL_PHASE_DURATION_SEC = 80;
const SLOW_PHASE_DURATION_SEC = 2 * 60;
// UI Update and Animation Timings (ms)
const PROGRESS_UPDATE_INTERVAL_MS = 100; // How often to update progress state
const FINAL_ANIMATION_DURATION_MS = 1000; // Duration of the final progress bar animation

// --- Reusable ProgressBar Component ---
const ProgressBar = ({ progress, transitionDurationMs }) => {
    // Ensure progress is clamped between 0 and 100
    const clampedProgress = Math.min(100, Math.max(0, progress));
    // Use the passed transition duration
    const transitionSeconds = transitionDurationMs / 1000;

    return (
        <div className="w-full max-w-md bg-muted rounded-full h-2.5 mb-6 dark:bg-gray-700 overflow-hidden">
            <div
                className="bg-purple-600 h-2.5 rounded-full"
                style={{
                    width: `${clampedProgress}%`,
                    // Use variable transition duration based on prop
                    transition: `width ${transitionSeconds}s ease-in-out`
                }}
                aria-valuenow={clampedProgress} // Accessibility improvements
                aria-valuemin="0"
                aria-valuemax="100"
                role="progressbar"
            ></div>
        </div>
    );
};
// --- End ProgressBar Component ---

// Props: generationId, onComplete(educatorProjectId), onError(message), onClose(), onRetry()
// Note: onComplete now likely receives the educatorProject ID based on the query
const LoadingStep = ({ generationId, onComplete, onError, onClose, onRetry, errorMessage }) => {
    // Initialize viewState based on errorMessage
    const [viewState, setViewState] = useState(errorMessage ? 'error' : 'loading');
    const [finalEducatorProject, setFinalEducatorProject] = useState(null); // Changed from lessonId
    const [finalErrorMessage, setFinalErrorMessage] = useState(errorMessage || '');

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFadingOut, setIsFadingOut] = useState(false);

    // Set refetchInterval to null if we have an error message to prevent unnecessary polling
    const [refetchInterval, setRefetchInterval] = useState(errorMessage ? null : POLLING_INTERVAL);

    const [progress, setProgress] = useState(0); // State remains here
    const [initialTargetProgress, setInitialTargetProgress] = useState(0);
    const startTimeRef = useRef(null);
    const successTimeoutRef = useRef(null); // Ref to store the success delay timeout ID

    // Effect to handle the errorMessage prop changes
    useEffect(() => {
        if (errorMessage) {
            setViewState('error');
            setFinalErrorMessage(errorMessage);
            setRefetchInterval(null);
            // Reset progress on initial error
            setProgress(0);
            startTimeRef.current = null;
            // Clear any pending success transition if an error occurs suddenly
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
                successTimeoutRef.current = null;
            }
        }
    }, [errorMessage]);

    // --- Callback to handle state transition (Simplified Error Handling) ---
    const handlePollingResult = useCallback((status, educatorProject) => {
        const upperStatus = status?.toUpperCase();


        if (upperStatus === 'COMPLETED') {
            if (educatorProject) {
                setFinalEducatorProject(educatorProject);
                setRefetchInterval(null);
                setProgress(100);
                // Delay switching to success view state
                successTimeoutRef.current = setTimeout(() => {
                    setViewState('success');
                    successTimeoutRef.current = null; // Clear ref after timeout runs
                }, FINAL_ANIMATION_DURATION_MS); // Wait for animation duration

            } else {
                // Error: Completed but the expected ID wasn't found in the response
                const msg = "An unknown error occurred. Please try again or contact support.";
                setFinalErrorMessage(msg);
                setViewState('error');
                setRefetchInterval(null);
                onError && onError(msg);
            }
        } else if (upperStatus === 'FAILED') {
            // Error: Backend explicitly marked it as failed
            const msg = "Lesson generation failed."; // Generic message as we don't fetch specific error message
            setFinalErrorMessage(msg);
            setViewState('error');
            onError && onError(msg);
            setRefetchInterval(null);
        }
        // If PENDING or PROCESSING, do nothing
    }, [onError]);

    // --- Effect for cycling loading messages (remains the same) ---
    useEffect(() => {
        let textIntervalId = null;
        if (viewState === 'loading') {
            textIntervalId = setInterval(() => {
                setIsFadingOut(true);
                const timeoutId = setTimeout(() => {
                    setCurrentIndex((prev) => (prev + 1) % loadingMessages.length);
                    setIsFadingOut(false);
                }, FADE_DURATION);
                return () => clearTimeout(timeoutId);
            }, 5000 - FADE_DURATION);
        }
        return () => {
            if (textIntervalId) clearInterval(textIntervalId);
        };
    }, [viewState]);

    // --- Progress Bar Logic Effect ---
    useEffect(() => { // Progress calculation effect
        let intervalId = null;

        if (viewState === 'loading') {
            if (startTimeRef.current === null) {
                startTimeRef.current = Date.now();
                setInitialTargetProgress(75 + Math.random() * 10);
                setProgress(0);
            }

            intervalId = setInterval(() => {
                // Only update progress if not already at 100% (waiting for success timeout)
                if (progress < 100) {
                    const now = Date.now();
                    // Calculate elapsed time in seconds
                    const elapsedSeconds = (now - startTimeRef.current) / 1000;
                    let currentProgress = 0;

                    if (elapsedSeconds < INITIAL_PHASE_DURATION_SEC) {
                        // Initial phase calculation based on seconds
                        currentProgress = (elapsedSeconds / INITIAL_PHASE_DURATION_SEC) * initialTargetProgress;
                    } else {
                        // Slow phase calculation based on seconds
                        const elapsedInSlowPhaseSec = elapsedSeconds - INITIAL_PHASE_DURATION_SEC;
                        const targetSlowProgress = 99; // Aim just below 100
                        const progressToAddInSlowPhase = (elapsedInSlowPhaseSec / SLOW_PHASE_DURATION_SEC) * (targetSlowProgress - initialTargetProgress);
                        currentProgress = initialTargetProgress + progressToAddInSlowPhase;
                    }
                    // Update progress, capping at 99 until explicitly set to 100 on completion
                    setProgress(Math.min(99, currentProgress));
                }
            }, PROGRESS_UPDATE_INTERVAL_MS);

        } else if (viewState === 'success') {
            setProgress(100); // Set target to 100% for final animation
            startTimeRef.current = null;
        } else if (viewState === 'error') {
            setProgress(0); // Reset progress on error
            startTimeRef.current = null;
        }

        return () => { // Cleanup
            if (intervalId) {
                clearInterval(intervalId);
            }

        };
    }, [viewState, initialTargetProgress, progress]); // Rerun when viewState changes OR when initialTargetProgress is set

    // // --- React Query Hook ---
    const {
        data: queryData,
        error: queryError, // Handles network/fetch errors
        isError,
    } = useQuery({ // Assuming this is the Tanstack React Query hook directly or a thin wrapper
        queryKey: ['generationStatus', generationId],
        gqlQuery: CHECK_GENERATION_STATUS_QUERY,
        variables: { id: generationId },
        // Only enable querying if we don't have an error message
        enabled: !!generationId && viewState === 'loading' && !errorMessage,
        refetchInterval: refetchInterval,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: false,

        onSuccess: (data) => {
            // Process the result when fetch succeeds
            const generationData = data?.educatorProjectGeneration;
            if (generationData) {
                // Call handler which updates viewState based on status
                handlePollingResult(generationData.status, generationData.educatorProject);
            } else if (viewState === 'loading') {
                // Handle case where query succeeded but returned unexpected null data
                handlePollingResult('FAILED');
            }
        },
        onError: (error) => {
            // Handles fetch/network errors
            if (viewState === 'loading') {
                const msg = error.message || "Error checking generation status.";
                setFinalErrorMessage(msg);
                setViewState('error');
                onError && onError(msg); // Also notify parent immediately
            }
            setRefetchInterval(null);
        }
    });

    // --- Event Handlers for Final States (Adapted for project ID) ---
    const handleContinue = () => {
        if (finalEducatorProject) {
            onComplete(finalEducatorProject); // Pass the educatorProject ID
        } else {
            onClose();
        }
    };

    const handleClose = () => {
        onClose();
    };

    // --- Render Logic (remains the same structure) ---
    const currentMessage = loadingMessages[currentIndex];

    // Determine transition duration based on state
    const progressTransitionDuration =
        (progress === 100 && viewState === 'loading') || viewState === 'success'
            ? FINAL_ANIMATION_DURATION_MS
            : PROGRESS_UPDATE_INTERVAL_MS;

    if (viewState === 'error') {
        // ... Error UI using finalErrorMessage ...
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-center w-full h-full p-4">
                <span className="text-6xl mb-6 block" role="img" aria-label="Worried face">😟</span>
                <h3 className="text-xl text-primary font-semibold mb-2">Something went wrong</h3>
                <p className="text-base text-primary/60 max-w-md mb-6">
                    {/* Display query error first if available, then finalErrorMessage */}
                    {isError && queryError?.message ? queryError.message : finalErrorMessage || "We couldn't generate your lesson this time."}
                </p>
                <div className="flex space-x-4">
                    {/* <Button type="button" onClick={handleRetry} variant="default" className="bg-gray-800 hover:bg-gray-700 text-white">Retry</Button> */}
                    <Button type="button" onClick={handleClose} variant="outline">Close</Button>
                </div>
            </div>
        );
    }

    if (viewState === 'success') {
        // ... Success UI ...
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-center w-full h-full p-4">
                {/* --- Use ProgressBar Component --- */}
                <span className="text-6xl mb-6 block" role="img" aria-label="Party popper">🎉</span>
                <h3 className="text-xl text-primary font-semibold mb-1">Successfully created lesson</h3>
                <h3 className="text-xl text-primary font-bold mb-4">{finalEducatorProject?.name || ''}</h3>
                <p className="text-base text-primary/60 max-w-md mb-8">Your lesson has been generated as a starting point. Take a moment to review and personalize it to perfectly match your teaching style and your students' needs.</p>
                <Button type="button" onClick={handleContinue} variant="outline">Continue</Button>
            </div>
        );
    }

    // Default: Loading state (remains the same)
    return (
        <div className="flex-1 flex flex-col items-center justify-center text-center w-full h-full">
            {/* --- Use ProgressBar Component --- */}


            {/* Animated Content Container */}
            <div
                className={`flex flex-col items-center text-center px-4 transition-opacity duration-${FADE_DURATION} ease-in-out ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
            >
                <span className="text-6xl mb-6 block [animation:var(--animate-pulse-emoji)]" role="img" aria-label="Loading Emoji">
                    {currentMessage.emoji}
                </span>
                <h3 className="text-xl text-primary font-semibold mb-2">{currentMessage.text}</h3>
            </div>
            {/* Optional: Display query errors during loading (consider if needed alongside error state) */}
            {isError && queryError && viewState === 'loading' && (
                <p className="text-red-500 text-sm mt-4 absolute bottom-4">Error: {queryError.message}</p>
            )}

            <div className="w-full max-w-md mt-4">
                <ProgressBar progress={progress} transitionDurationMs={progressTransitionDuration} />
            </div>

            <p className="text-base text-primary/60 max-w-md">This can take up to 2-3 minutes. Feel free to grab a coffee or tea while you wait.</p>
        </div>
    );
};


export default LoadingStep;
